// pages/SAPOrgSection.jsx — per-organisation breakdown inside the "Master data & settings" card.
// Material / movement / storage location are plant-level; vendor & release are purchasing-org
// level (plus company-level vendor data); customer & output are sales-org level.
// Data: sapOrgMasterData.js + sapOrgNotes.js (snapshot 29-10-2026). Bilingual ID/EN.
import { useMemo } from 'react';
import { useProcMapLive, mergeLive } from './sapProcessMapLive';
import SAPSlocDetail from './SAPSlocDetail';
import SAPGlCompare from './SAPGlCompare';

const T = {
  en: {
    level: { plant: 'Per plant', porg: 'Per purchasing org', sorg: 'Per sales org', co: 'Per company code' },
    why: {
      material: 'Batch, QM, MRP, procurement type and price control are set per plant (MARC/MBEW), so the same material can behave differently in each plant.',
      movement: 'Goods movements are posted per plant. Shown: September 2026 items.',
      sloc: 'Storage locations belong to a plant (T001L). "Used" = appeared in a September 2026 goods movement.',
      release: 'Release strategies are determined per purchase document using purchasing org, plant, document type and value. Shown: Jul–Sep 2026.',
      vendor: 'A vendor has a general part, a purchasing part per purchasing org (LFM1) and an accounting part per company code (LFB1).',
      customer: 'A customer has a sales part per sales org (KNVV) and an accounting part per company code (KNB1). Materials are released for sale per sales org (MVKE).',
      output: 'Output (printed forms) is determined per sales org for sales documents and per purchasing org for purchasing documents. Shown: output records Jul–Sep 2026.',
      other: 'G/L accounts are opened per company code (SKB1).',
    },
    org: 'Org', mat: 'Materials', batch: 'Batch', qm: 'QM', mrp: 'MRP (PD · V1 · ND · none)', proc: 'Procure (F · E)', price: 'Price (S · V)',
    mv: 'Items Sep', mvD: 'Mvt types', top: 'Most used', slocDef: 'Defined', slocUsed: 'Used', po: 'POs', poRel: 'With release', strat: 'Strategies used (group/code)',
    pr: 'PRs', prRel: 'With release', prOrig: 'Origin (manual · order · sales order)', lfm: 'Vendors (purch. org)', info: 'Info records', vUsed: 'Vendors with PO',
    lfb: 'Vendors (company)', knb: 'Customers (company)', gl: 'G/L accounts', knvv: 'Customers', mvke: 'Materials for sale', so: 'Sales orders', cUsed: 'Customers ordering',
    outS: 'Sales output', outE: 'Purchasing output', none: '—', only: 'Only company', all: 'Show all', findings: 'Differences between orgs', prTitle: 'Purchase requisitions per plant',
  },
  id: {
    level: { plant: 'Per plant', porg: 'Per purchasing org', sorg: 'Per sales org', co: 'Per company code' },
    why: {
      material: 'Batch, QM, MRP, procurement type dan price control di-setting per plant (MARC/MBEW), jadi material yang sama bisa berperilaku berbeda di tiap plant.',
      movement: 'Pergerakan barang diposting per plant. Ditampilkan: item September 2026.',
      sloc: 'Storage location milik sebuah plant (T001L). "Dipakai" = muncul di pergerakan barang September 2026.',
      release: 'Release strategy ditentukan per dokumen pembelian berdasarkan purchasing org, plant, tipe dokumen dan nilai. Ditampilkan: Jul–Sep 2026.',
      vendor: 'Vendor punya data umum, data pembelian per purchasing org (LFM1) dan data akuntansi per company code (LFB1).',
      customer: 'Customer punya data sales per sales org (KNVV) dan data akuntansi per company code (KNB1). Material dibuka untuk dijual per sales org (MVKE).',
      output: 'Output (form cetak) ditentukan per sales org untuk dokumen penjualan dan per purchasing org untuk dokumen pembelian. Ditampilkan: record output Jul–Sep 2026.',
      other: 'Akun G/L dibuka per company code (SKB1).',
    },
    org: 'Org', mat: 'Material', batch: 'Batch', qm: 'QM', mrp: 'MRP (PD · V1 · ND · kosong)', proc: 'Pengadaan (F · E)', price: 'Harga (S · V)',
    mv: 'Item Sep', mvD: 'Jenis mvt', top: 'Paling sering', slocDef: 'Didefinisikan', slocUsed: 'Dipakai', po: 'PO', poRel: 'Dengan release', strat: 'Strategi dipakai (group/kode)',
    pr: 'PR', prRel: 'Dengan release', prOrig: 'Asal (manual · order · sales order)', lfm: 'Vendor (purch. org)', info: 'Info record', vUsed: 'Vendor dengan PO',
    lfb: 'Vendor (company)', knb: 'Customer (company)', gl: 'Akun G/L', knvv: 'Customer', mvke: 'Material dijual', so: 'Sales order', cUsed: 'Customer yang order',
    outS: 'Output sales', outE: 'Output pembelian', none: '—', only: 'Hanya company', all: 'Tampilkan semua', findings: 'Perbedaan antar org', prTitle: 'Purchase requisition per plant',
  },
};

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

function Pct({ a, b, fmt }) {
  if (!b) return <span className="text-slate-300">—</span>;
  const p = pct(a, b);
  return (
    <span className="inline-flex items-center gap-1.5 justify-end w-full">
      <span className="font-mono tabular-nums">{fmt(a)}</span>
      <span className={`text-[10.5px] px-1 rounded ${p >= 50 ? 'bg-brand-50 text-brand-700' : p > 0 ? 'bg-slate-100 text-slate-500' : 'text-slate-300'}`}>{p}%</span>
    </span>
  );
}

function Chips({ items, fmt, label = x => x[0], title = x => x[1] }) {
  if (!items || !items.length) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((x, i) => (
        <span key={i} title={title(x)} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-[1px] text-[11px]">
          <b className="font-mono text-slate-700">{label(x)}</b>
          {x[1] && typeof x[1] === 'string' && <span className="text-slate-500 truncate max-w-[130px]">{x[1]}</span>}
          <span className="font-mono text-slate-400">{fmt(x[x.length - 1])}</span>
        </span>
      ))}
    </div>
  );
}

function Table({ title, cols, rows, coCode }) {
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-[12.5px] border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-[11px]">
              {cols.map((c, i) => (
                <th key={i} className={`px-2.5 py-1.5 font-semibold whitespace-nowrap ${c.left ? 'text-left' : 'text-right'}`}>{c.h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.k} className={`border-t border-slate-100 ${coCode && r.co === coCode ? 'bg-brand-50' : ''}`}>
                {cols.map((c, i) => (
                  <td key={i} className={`px-2.5 py-1.5 align-top ${c.left ? 'text-left' : 'text-right'} ${c.wide ? 'min-w-[220px]' : 'whitespace-nowrap'}`}>{c.v(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SAPOrgSection({ landKey, tab, lang, coCode, onlyCo, setOnlyCo }) {
  const live = useProcMapLive();
  const { ORG, ORGN: NOTES } = useMemo(() => mergeLive(live), [live]);
  const L = lang === 'id' ? 'id' : 'en';
  const t = T[L];
  const data = ORG[landKey];
  if (!data) return null;
  const fmt = n => (typeof n === 'number' ? n.toLocaleString(L === 'id' ? 'id-ID' : 'en-US') : n);
  const filt = rows => (coCode && onlyCo ? rows.filter(r => r.co === coCode) : rows);
  const P = filt(data.plants), E = filt(data.porgs), S = filt(data.sorgs), C = filt(data.companies);

  const orgCol = { h: t.org, left: true, v: r => (
    <span className="flex flex-col leading-tight">
      <b className="font-mono text-slate-800">{r.k}</b>
      {r.n && <span className="text-[11px] text-slate-500 truncate max-w-[180px]" title={r.n}>{r.n}</span>}
    </span>) };
  const num = (h, f) => ({ h, v: r => <span className="font-mono tabular-nums">{fmt(f(r))}</span> });
  const trio = (h, f) => ({ h, v: r => <span className="font-mono tabular-nums text-slate-700">{f(r).map(fmt).join(' · ')}</span> });

  const tables = [];
  if (tab === 'material') {
    tables.push({ title: t.level.plant, rows: P, cols: [orgCol, num(t.mat, r => r.mat),
      { h: t.batch, v: r => <Pct a={r.batch} b={r.mat} fmt={fmt} /> }, { h: t.qm, v: r => <Pct a={r.qm} b={r.mat} fmt={fmt} /> },
      trio(t.mrp, r => [r.pd, r.v1, r.nd, r.mrpE]), trio(t.proc, r => [r.F, r.E]), trio(t.price, r => [r.S, r.V])] });
  } else if (tab === 'movement') {
    tables.push({ title: t.level.plant, rows: P, cols: [orgCol, num(t.mv, r => r.mv), num(t.mvD, r => r.mvD),
      { h: t.top, left: true, wide: true, v: r => <Chips items={r.mvTop} fmt={fmt} /> }] });
  } else if (tab === 'sloc') {
    tables.push({ title: t.level.plant, rows: P, cols: [orgCol, num(t.slocDef, r => r.sloc), { h: t.slocUsed, v: r => <Pct a={r.slocU} b={r.sloc} fmt={fmt} /> }] });
  } else if (tab === 'release') {
    tables.push({ title: t.level.porg, rows: E, cols: [orgCol, num(t.po, r => r.po), { h: t.poRel, v: r => <Pct a={r.poRel} b={r.po} fmt={fmt} /> },
      { h: t.strat, left: true, wide: true, v: r => <Chips items={r.relTop} fmt={fmt} /> }] });
    tables.push({ title: t.prTitle, rows: P, cols: [orgCol, num(t.pr, r => r.pr.n), { h: t.prRel, v: r => <Pct a={r.pr.rel} b={r.pr.n} fmt={fmt} /> },
      trio(t.prOrig, r => [r.pr.man, r.pr.ord, r.pr.sd])] });
  } else if (tab === 'vendor') {
    tables.push({ title: t.level.porg, rows: E, cols: [orgCol, num(t.lfm, r => r.vendors), num(t.info, r => r.info), num(t.po, r => r.po),
      { h: t.vUsed, v: r => <Pct a={r.vendorsUsed} b={r.vendors} fmt={fmt} /> }] });
    tables.push({ title: t.level.co, rows: C, cols: [orgCol, num(t.lfb, r => r.vendors)] });
  } else if (tab === 'customer') {
    tables.push({ title: t.level.sorg, rows: S, cols: [orgCol, num(t.knvv, r => r.customers), num(t.mvke, r => r.materials), num(t.so, r => r.so),
      { h: t.cUsed, v: r => <Pct a={r.custUsed} b={r.customers} fmt={fmt} /> }] });
    tables.push({ title: t.level.co, rows: C, cols: [orgCol, num(t.knb, r => r.customers)] });
  } else if (tab === 'output') {
    tables.push({ title: `${t.level.sorg} · ${t.outS}`, rows: S, cols: [orgCol,
      { h: t.top, left: true, wide: true, v: r => <Chips items={r.out} fmt={fmt} label={x => x[0].split('/')[1]} title={x => x[0]} /> }] });
    tables.push({ title: `${t.level.porg} · ${t.outE}`, rows: E, cols: [orgCol,
      { h: t.top, left: true, wide: true, v: r => <Chips items={r.out} fmt={fmt} label={x => x[0].split('/')[1]} title={x => x[0]} /> }] });
  } else if (tab === 'other') {
    tables.push({ title: t.level.co, rows: C, cols: [orgCol, num(t.gl, r => r.gl), num(t.lfb, r => r.vendors), num(t.knb, r => r.customers)] });
  }

  const notes = (NOTES[landKey] && NOTES[landKey][tab]) || [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[12px] text-slate-500 m-0 flex-1 min-w-[240px]">{t.why[tab]}</p>
        {coCode && (
          <button type="button" onClick={() => setOnlyCo(v => !v)}
            className={`px-2.5 py-1 rounded-md text-[12px] font-semibold border ${onlyCo ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-700 border-slate-200'}`}>
            {onlyCo ? `✓ ${t.only} ${coCode}` : `${t.only} ${coCode}`}
          </button>
        )}
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))' }}>
        {tables.map((tb, i) => <Table key={i} title={tb.title} cols={tb.cols} rows={tb.rows} coCode={coCode} />)}
      </div>
      {tab === 'sloc' && <SAPSlocDetail landKey={landKey} plants={P} lang={L} />}
      {tab === 'other' && <SAPGlCompare landKey={landKey} lang={L} />}
      {notes.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-100 px-3.5 py-2.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1">{t.findings}</div>
          <ul className="list-disc pl-4 text-[12.5px] text-slate-700 space-y-1">{notes.map((n, i) => <li key={i}>{n[L]}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
